import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, Wallet, AlertCircle, HandCoins, FilePlus2, PackagePlus, ScanBarcode, Truck, Users, Building2, PackageX, ArrowRight, Zap, Receipt, BarChart3 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { Loader } from "../components/kit";

const QUICK = [
  { to: "/memo/new", label: "নতুন মেমো", icon: FilePlus2, id: "quick-create-memo-button", primary: true },
  { to: "/products/new", label: "পণ্য যোগ", icon: PackagePlus, id: "quick-add-product-button" },
  { to: "/collection", label: "টাকা আদায়", icon: HandCoins, id: "quick-collection-button" },
  { to: "/purchase/new", label: "ক্রয় এন্ট্রি", icon: Truck, id: "quick-purchase-button" },
  { to: "/barcode-sale", label: "বারকোড সেল", icon: ScanBarcode, id: "quick-barcode-button" },
];

const ACCENT: Record<string, { ring: string; glow: string; text: string }> = {
  blue: { ring: "from-blue-400/30 to-blue-600/10", glow: "bg-blue-500/20 text-blue-300", text: "text-white" },
  green: { ring: "from-emerald-400/30 to-emerald-600/10", glow: "bg-emerald-500/20 text-emerald-300", text: "text-emerald-300" },
  red: { ring: "from-rose-400/30 to-rose-600/10", glow: "bg-rose-500/20 text-rose-300", text: "text-rose-300" },
  amber: { ring: "from-amber-400/30 to-amber-600/10", glow: "bg-amber-500/20 text-amber-300", text: "text-amber-300" },
  slate: { ring: "from-slate-300/20 to-slate-500/10", glow: "bg-slate-400/20 text-slate-200", text: "text-white" },
};

function GlassStat({ label, value, icon: Icon, tone = "blue", testid, hint, to }: any) {
  const a = ACCENT[tone];
  const cls = `relative block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 md:p-5 fade-up ${to ? "cursor-pointer transition-all hover:bg-white/[0.07] hover:border-white/20 active:scale-[0.98]" : ""}`;
  const inner = (
    <>
      <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br ${a.ring} blur-2xl pointer-events-none`} />
      <div className="relative flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-300">{label}</p>
        {Icon && <span className={`w-9 h-9 rounded-xl grid place-items-center ${a.glow}`}><Icon size={18} /></span>}
      </div>
      <p className={`num mt-2 text-xl md:text-2xl font-bold ${a.text}`} data-testid={testid ? `${testid}-value` : undefined}>{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testid}>{inner}</Link>;
  return <div className={cls} data-testid={testid}>{inner}</div>;
}

function Panel({ title, link, linkText, children, testid }: any) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden fade-up">
      <div className="flex justify-between items-center px-5 py-4 border-b border-white/10">
        <h2 className="text-[16px] font-semibold text-white">{title}</h2>
        {link && <Link to={link} className="text-sm text-blue-300 font-semibold hover:text-blue-200 inline-flex items-center gap-1">{linkText} <ArrowRight size={14} /></Link>}
      </div>
      {children}
    </div>
  );
}

const EmptyDark = ({ text }: { text: string }) => <div className="py-10 text-center text-slate-500 text-sm">{text}</div>;

const RANGES = [
  { d: 7, label: "৭ দিন" },
  { d: 14, label: "১৪ দিন" },
  { d: 30, label: "৩০ দিন" },
];

const dayLabel = (iso: string) => bn(`${iso.slice(8, 10)}/${iso.slice(5, 7)}`);

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-white/15 bg-slate-900/90 backdrop-blur-xl px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-semibold mb-1">{dayLabel(label)}</p>
      <p className="text-blue-300">বিক্রি: <b className="num">{tk(row.sale)}</b></p>
      <p className="text-emerald-300">আদায়: <b className="num">{tk(row.collection)}</b></p>
      <p className="text-rose-300">খরচ: <b className="num">{tk(row.expense)}</b></p>
    </div>
  );
}

function SalesChart() {
  const [days, setDays] = useState(14);
  const [ov, setOv] = useState<any>(null);
  useEffect(() => {
    setOv(null);
    api.get(`/analytics/overview?days=${days}`).then((r) => setOv(r.data));
  }, [days]);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden fade-up mt-3 md:mt-4" data-testid="sales-chart-panel">
      <div className="flex flex-wrap justify-between items-center gap-2 px-5 py-4 border-b border-white/10">
        <h2 className="text-[16px] font-semibold text-white inline-flex items-center gap-2">
          <BarChart3 size={18} className="text-blue-300" /> বিক্রির ধারা
          {ov && <span className="text-xs font-normal text-slate-400">• এ মাসে বিক্রি {tk(ov.month.sale)}</span>}
        </h2>
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10">
          {RANGES.map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              data-testid={`chart-range-${r.d}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${days === r.d ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-white/10"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-3 md:p-4">
        {!ov ? (
          <div className="h-[260px] grid place-items-center"><Loader /></div>
        ) : (
          <div style={{ width: "100%", height: 260 }} data-testid="sales-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ov.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gSale" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gColl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={16} />
                <YAxis tickFormatter={(v) => bn(v >= 1000 ? `${Math.round(v / 1000)}k` : v)} tick={{ fill: "#94a3b8", fontSize: 11 }} tickLine={false} axisLine={false} width={46} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="sale" stroke="#60a5fa" strokeWidth={2.5} fill="url(#gSale)" />
                <Area type="monotone" dataKey="collection" stroke="#34d399" strokeWidth={2} fill="url(#gColl)" />
                <Area type="monotone" dataKey="expense" stroke="#fb7185" strokeWidth={2} fill="transparent" strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard({ demo }: { demo?: any }) {
  const [d, setD] = useState<any>(demo ?? null);
  const nav = useNavigate();
  useEffect(() => {
    if (!demo) api.get("/dashboard").then((r) => setD(r.data));
  }, [demo]);
  if (!d) return <Loader />;

  return (
    <div data-testid="shop-dashboard" className="relative -m-4 md:-m-6 lg:-m-8 p-4 md:p-6 lg:p-8 min-h-[calc(100vh-4rem)] overflow-hidden"
      style={{ background: "radial-gradient(1200px 600px at 15% -5%, #163a6e 0%, transparent 55%), radial-gradient(900px 500px at 100% 0%, #0d5c3a 0%, transparent 50%), linear-gradient(160deg, #0a1326 0%, #0b1a33 60%, #0a1326 100%)" }}>

      {/* hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-blue-600/20 via-white/[0.04] to-emerald-600/10 backdrop-blur-xl p-5 md:p-7 mb-5 fade-up">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 left-20 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-blue-200 bg-blue-500/10 border border-blue-400/20 rounded-full px-3 py-1 mb-3">
              <Zap size={13} className="text-emerald-300" fill="currentColor" /> রানা ইলেক্ট্রিক ড্যাশবোর্ড
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-white" data-testid="page-title">আজকের সারসংক্ষেপ</h1>
            <p className="text-sm text-slate-300 mt-1">তারিখ: {fmtDate(d.date)} • {bn(d.memo_count)} টি মেমো আজ</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK.map((q) => (
              <Link key={q.to} to={q.to} data-testid={q.id}
                className={`inline-flex items-center gap-2 h-11 px-4 rounded-xl text-[15px] font-semibold transition-all active:scale-95 ${
                  q.primary ? "bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/25"
                  : "bg-white/5 text-slate-200 border border-white/10 hover:bg-white/10 backdrop-blur"}`}>
                <q.icon size={17} /> {q.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <GlassStat label="আজকের মোট বিক্রি" value={tk(d.total_sale)} icon={TrendingUp} tone="blue" testid="stat-today-sale" hint={`${bn(d.memo_count)} টি মেমো`} to="/memos" />
        <GlassStat label="বিক্রিতে পরিশোধ" value={tk(d.total_paid)} icon={Wallet} tone="green" testid="stat-today-paid" to="/daily" />
        <GlassStat label="আজকের নতুন বাকি" value={tk(d.total_due)} icon={AlertCircle} tone="red" testid="stat-today-due" to="/customers" />
        <GlassStat label="আজকের বাকি আদায়" value={tk(d.total_collection)} icon={HandCoins} tone="green" testid="stat-today-collection" hint={`মোট ক্যাশ: ${tk(d.cash_in)}`} to="/collection" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-3 md:mt-4">
        <GlassStat label="ক্রেতাদের কাছে মোট পাওনা" value={tk(d.total_receivable)} icon={Users} tone="red" testid="stat-total-receivable" to="/customers" />
        <GlassStat label="কোম্পানির কাছে দেনা" value={tk(d.total_payable)} icon={Building2} tone="slate" testid="stat-total-payable" to="/companies" />
        <GlassStat label="আজকের খরচ" value={tk(d.total_expense || 0)} icon={Receipt} tone="amber" testid="stat-today-expense" to="/expenses" />
        <GlassStat label="স্টক সতর্কতা" value={`${bn(d.low_stock.length)} টি পণ্য`} icon={PackageX} tone="amber" testid="stat-low-stock" to="/stock" />
      </div>

      {/* sales chart */}
      <SalesChart />

      {/* lower panels */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mt-5">
        <Panel title="সাম্প্রতিক মেমো" link="/memos" linkText="সব দেখুন">
          {d.recent_memos.length === 0 ? <EmptyDark text="এখনো কোনো মেমো নেই" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recent-memos-table">
                <thead>
                  <tr className="text-slate-400 text-left">
                    <th className="px-5 py-2.5 font-medium">মেমো নং</th><th className="px-3 py-2.5 font-medium">ক্রেতা</th>
                    <th className="px-3 py-2.5 font-medium">তারিখ</th><th className="px-3 py-2.5 font-medium text-right">মোট</th>
                    <th className="px-5 py-2.5 font-medium text-right">বাকি</th>
                  </tr>
                </thead>
                <tbody>
                  {d.recent_memos.map((m: any) => (
                    <tr key={m.id} className="border-t border-white/5 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => nav(`/memos/${m.id}`)} data-testid={`recent-memo-${m.invoice_no}`}>
                      <td className="px-5 py-3 font-semibold text-blue-300">{m.invoice_no}</td>
                      <td className="px-3 py-3 text-slate-200">{m.customer?.name}</td>
                      <td className="px-3 py-3 num text-slate-300">{fmtDate(m.date)}</td>
                      <td className="px-3 py-3 text-right num font-semibold text-white">{tk(m.grand_total)}</td>
                      <td className={`px-5 py-3 text-right num font-semibold ${m.current_due > 0 ? "text-rose-300" : "text-emerald-300"}`}>{tk(m.current_due)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <div className="space-y-4">
          <Panel title="বাকি আছে এমন ক্রেতা" link="/customers" linkText="খাতা">
            {d.due_customers.length === 0 ? <EmptyDark text="কারো বাকি নেই" /> : (
              <ul className="divide-y divide-white/5" data-testid="due-customer-list">
                {d.due_customers.map((c: any) => (
                  <li key={c.id}>
                    <Link to={`/customers/${c.id}`} className="flex justify-between px-5 py-3 hover:bg-white/5 transition-colors">
                      <span><b className="font-semibold text-slate-100">{c.name}</b><span className="block text-xs text-slate-400">{bn(c.mobile)}</span></span>
                      <span className="num font-bold text-rose-300">{tk(c.due)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="স্টক কম" link="/stock" linkText="স্টক">
            {d.low_stock.length === 0 ? <EmptyDark text="সব পণ্যের স্টক ঠিক আছে" /> : (
              <ul className="divide-y divide-white/5" data-testid="low-stock-list">
                {d.low_stock.slice(0, 6).map((p: any) => (
                  <li key={p.id} className="flex justify-between items-center px-5 py-2.5 text-sm">
                    <span className="text-slate-200">{p.name}</span>
                    <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300">{bn(p.stock)} {p.unit}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
